import React, { useState, useEffect } from 'react';
import propTypes from 'prop-types';
import { Box, TableCell, Typography, TextField, Tooltip, CircularProgress } from '@mui/material';
import Logger from '../../lib/Logger';
import { renderErrorMessage, renderSuccessMessage } from '../../lib/toastMessage';
import dayjs from 'dayjs';

// import FilterAltOutlinedIcon from '@mui/icons-material/FilterAltOutlined';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import RemoveCircleIcon from '@mui/icons-material/RemoveCircle';
import DeleteIcon from '@mui/icons-material/Delete';
// import { CustomMenu } from 'src/components/menu/customMenu';
import { LoadingButton } from '@mui/lab';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
// import { axiosVBGetCall } from '../../../services/vbApiCall';
import { PaginationTable } from './paginationTable';
import { axiosGetCall, axiosPostCall } from 'src/services/apisCall';

const BasicDatePicker = ({ value, onChange }) => {
  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <DatePicker
        slotProps={{
          textField: { size: 'small' },
          actionBar: {
            actions: ['today', 'clear'],
          },
        }}
        sx={{ width: '270px', margin: '8px' }}
        value={value}
        onChange={(newValue) => onChange(newValue)}
      />
    </LocalizationProvider>
  );
};

BasicDatePicker.propTypes = {
  value: propTypes.number,
  onChange: propTypes.func.isRequired,
};

export const AmountPlanTable = ({ amount = [], user = {}, forceLoad, loading: tableLoading }) => {
  const [rows, setRows] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const logger = new Logger('senderId');
  const handleFieldChange = (rowIndex, key, value) => {
    setRows((prevRows) => {
      const newRows = [...prevRows];
      newRows[rowIndex] = { ...newRows[rowIndex], [key]: value };
      return newRows;
    });
  };

  const handleUpdateCredits = async (plan, credit, validity, setLoading, type) => {
    setLoading(true);

    try {
      if (credit === 0) {
        setLoading(false);
        return renderErrorMessage('Credit should be more than 0.');
      }
      const {
        voiceInfo: { userId },
      } = user;

      if (userId && type) {
        const payload = {
          assignTo: userId,
          userType: 'reseller',
          assignAmount: credit,
          assignValidity: validity,
          type,
        };
        const { message } = await axiosGetCall('assignAmount', payload);

        forceLoad();
        renderSuccessMessage(message);
      }
    } catch (error) {
      logger.error('userPlanTable.jsx ,handleUpdateCredits ,assignCreditToUser api', error);
      renderErrorMessage(error?.message);
    }

    setLoading(false);
  };

  useEffect(() => {
    if (Array.isArray(amount)) {
      setRows(amount);
    } else {
      console.warn('⚠️ Invalid amount data (not an array):', amount);
      setRows([]); // fallback to empty array
    }
  }, [amount]);

  const columns = [
    { id: 'totalAmount', label: 'Total Amount', minWidth: 100, align: 'center' },
    { id: 'reservedAmount', label: 'Reserved Amount', align: 'center', minWidth: 100 },
    { id: 'currentAmount', label: 'Current Amount', align: 'center', minWidth: 100 },

    {
      id: 'Validity',
      label: 'Validity',
      minWidth: 60,
      align: 'center',
    },

    {
      id: 'actions',
      label: 'Action',
      minWidth: 80,
      align: 'center',
    },
  ];

  const ActionJsx = ({ id, align, value, row }) => {
    const [creditValue, setCreditValue] = useState(0);
    const [validity, setValidity] = useState(row.validity);

    const [assignLoading, setAssignLoading] = useState(false);
    const [deductLoading, setDeductLoading] = useState(false);
    const [removePlanLoading, setRemovePlanLoading] = useState(false);

    return (
      <TableCell
        key={id}
        align={align}
        sx={{
          color: 'grey.400',
          borderBottom: '0.5px dashed',
          borderColor: 'grey.700',
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 1 }}>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DatePicker
              label="Validity"
              value={validity ? dayjs.unix(validity) : null}
              onChange={(newValue) => setValidity(newValue ? newValue.startOf('day').unix() : null)}
              shouldDisableDate={(date) => dayjs(date).isBefore(dayjs().startOf('day'))}
            />
          </LocalizationProvider>
          <TextField
            id="outlined-basic"
            type="number"
            label="Amount"
            value={creditValue}
            onChange={(e) => setCreditValue(e.target.value)}
            size="large"
            variant="outlined"
            sx={{ width: '150px' }}
          />
          <Box>
            <Tooltip title="Add Amount">
              <LoadingButton
                style={{
                  width: '30px',
                  minWidth: '30px',
                }}
                size="small"
                color="success"
                onClick={() => handleUpdateCredits(row, creditValue, validity, setAssignLoading, 'A')}
                loading={assignLoading}
                loadingIndicator={<CircularProgress style={{ color: '#22C55E' }} size={16} />}
              >
                <AddCircleIcon />
              </LoadingButton>
            </Tooltip>
            <Tooltip title="Deduct Amount">
              <LoadingButton
                style={{
                  width: '30px',
                  minWidth: '30px',
                }}
                size="small"
                color="error"
                loading={deductLoading}
                onClick={() => handleUpdateCredits(row, creditValue, validity, setDeductLoading, 'D')}
                loadingIndicator={<CircularProgress style={{ color: '#FF5630' }} size={16} />}
              >
                <RemoveCircleIcon />
              </LoadingButton>
            </Tooltip>
          </Box>
        </Box>
      </TableCell>
    );
  };

  return (
    <Box>
      <Typography
        id="modal-modal-title"
        variant="h6"
        component="h2"
        sx={{ mb: '-20px' }}
        // sx={{ color: 'grey.10' }}
      >
        Amount
      </Typography>
      <PaginationTable
        page={page}
        ActionJsx={ActionJsx}
        setPage={setPage}
        rowsPerPage={rowsPerPage}
        setRowsPerPage={setRowsPerPage}
        loading={tableLoading}
        rows={rows}
        columns={columns}
        maxHeight={680}
        mapKey="planName"
        onRowFieldChange={handleFieldChange}
      />
    </Box>
  );
};
